import {inject, injectable} from "inversify";
import {Player, Team} from "@/modules/team/entity";
import {
  TransferListing,
  TransferOffer,
  TransferPlayerNegotiation,
  TransferTeamNegotiation
} from "@/modules/transfer-market/entity";
import {EventEmitter} from "@/modules/base/events";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {Season} from "@/modules/season/entity";
import {BeforeTransferOfferCreateEvent} from "@/modules/transfer-market/events/BeforeTransferOfferCreateEvent";
import {AfterTransferOfferCreateEvent} from "@/modules/transfer-market/events/AfterTransferOfferCreateEvent";
import {NegotiationStatusEnum} from "@/modules/diplomacy/enums";

@injectable()
export default class OfferCreator {
  constructor(
    @inject(EventEmitter) private em: EventEmitter,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {
  }

  public async create(player: Player, team: Team, price: number, listing?: TransferListing) {
    if (player.transferred) {
      throw 'Player has been transferred this season already.';
    }

    const exists = await TransferOffer
      .createQueryBuilder('offer')
      .innerJoin('offer.team', 'team')
      .innerJoin('offer.player', 'player')
      .where('team.id = :teamId AND player.id = :playerId', {teamId: team.id, playerId: player.id})
      .getCount() > 0;

    if (exists) {
      throw 'There is already an existing offer.';
    }

    await this.em.emitAsync(new BeforeTransferOfferCreateEvent(player, listing));

    const season = await Season.current();

    const offer = new TransferOffer();
    offer.listing = listing;
    offer.player = player;
    offer.team = team;
    offer.playersTeam = player.team;
    offer.price = price;
    await offer.save();

    let teamNegotiation;

    if (player.team) {
      teamNegotiation = new TransferTeamNegotiation();
      teamNegotiation.offer = offer;
      teamNegotiation.target = player.team && player.team.user ? team : player.team;
      teamNegotiation.season = season;
      teamNegotiation.monthlyFrom = 0;
      teamNegotiation.monthlyTo = 0;

      if (player.team && player.team.user) {
        teamNegotiation.team = player.team;
        teamNegotiation.onceFrom = price;
        teamNegotiation.onceTo = price * 1.2;
      } else {
        teamNegotiation.team = team;
        teamNegotiation.onceFrom = price;
        teamNegotiation.onceTo = price * 0.8;
      }

      await teamNegotiation.save();
    }

    const playerNegotiation = new TransferPlayerNegotiation();
    playerNegotiation.offer = offer;
    playerNegotiation.target = player;
    playerNegotiation.team = team;
    playerNegotiation.season = season;
    playerNegotiation.monthlyFrom = 1000;
    playerNegotiation.monthlyTo = 500;
    playerNegotiation.onceFrom = 0;
    playerNegotiation.onceTo = 0;

    if (player.team && player.team.user) {
      playerNegotiation.status = NegotiationStatusEnum.CLOSED;
    }

    await playerNegotiation.save();

    await this.em.emitAsync(new AfterTransferOfferCreateEvent(player, offer, listing));

    return offer;
  }
}
